import { ArrowRight } from "lucide-react";
import { useContent, renderRich } from "@/context/ContentContext";

/**
 * Line-art bottle icon — each step shows a stylized stainless flask outline
 * built from CSS-only SVG strokes. Cycle-shape varies subtly by index for
 * a sense of "evolving" geometry across the line.
 */
const BottleIcon = ({ variant = 0, className = "" }) => {
    const variants = [
        // 1) raw straight tube
        <g key="t" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round">
            <rect x="22" y="6" width="20" height="52" rx="1" />
            <line x1="22" y1="12" x2="42" y2="12" />
        </g>,
        // 2) tube with one rolled end
        <g key="r" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round">
            <rect x="22" y="6" width="20" height="52" rx="1" />
            <ellipse cx="32" cy="10" rx="11" ry="3.5" />
        </g>,
        // 3) bulged body
        <g key="b" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round">
            <path d="M22 10 Q14 32 22 56 L42 56 Q50 32 42 10 Z" />
            <ellipse cx="32" cy="10" rx="10" ry="3" />
        </g>,
        // 4) bottle silhouette - necked
        <g key="n" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M27 4 H37 V14 Q44 18 44 28 V54 Q44 60 38 60 H26 Q20 60 20 54 V28 Q20 18 27 14 Z" />
            <line x1="27" y1="9" x2="37" y2="9" />
        </g>,
        // 5) bottle with threads
        <g key="th" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M27 4 H37 V14 Q44 18 44 28 V54 Q44 60 38 60 H26 Q20 60 20 54 V28 Q20 18 27 14 Z" />
            <line x1="27" y1="6" x2="37" y2="6" />
            <line x1="27" y1="9" x2="37" y2="9" />
            <line x1="27" y1="12" x2="37" y2="12" />
        </g>,
        // 6) finished bottle (filled shoulder accent)
        <g key="f" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M27 4 H37 V14 Q44 18 44 28 V54 Q44 60 38 60 H26 Q20 60 20 54 V28 Q20 18 27 14 Z" />
            <circle cx="32" cy="36" r="3" fill="currentColor" />
        </g>,
    ];
    return (
        <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
            {variants[variant % variants.length]}
        </svg>
    );
};

const ProcessFlow = ({ flow, idx }) => (
    <div className="border border-gray-200 bg-white p-6 md:p-8" data-testid={`process-flow-${idx}`}>
        <div className="flex items-baseline gap-3 mb-6">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-red-600">
                {String(idx + 1).padStart(2, "0")}
            </span>
            <h3 className="font-display font-bold uppercase tracking-tight text-xl md:text-2xl text-gray-900">
                {flow.title}
            </h3>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2 md:gap-3">
            {(flow.steps || []).map((step, i) => (
                <div key={i} className="relative group" data-testid={`process-step-${idx}-${i}`}>
                    <div className="border border-gray-200 bg-white aspect-square flex items-center justify-center transition-colors group-hover:border-red-600 group-hover:bg-red-50/50">
                        <BottleIcon variant={i} className="w-10 h-10 text-gray-700 group-hover:text-red-600 transition-colors" />
                    </div>
                    <div className="mt-2 text-center">
                        <div className="font-mono text-[9px] text-gray-400 mb-1">
                            ▲ STEP {String(i + 1).padStart(2, "0")}
                        </div>
                        <div className="text-[11px] md:text-xs text-gray-700 leading-tight font-medium">
                            {step}
                        </div>
                    </div>
                    {/* connector arrow - hide on last step */}
                    {i < (flow.steps?.length || 0) - 1 && (
                        <div className="hidden md:flex absolute top-[28%] -right-2 z-10 pointer-events-none">
                            <ArrowRight size={14} className="text-red-600/60" />
                        </div>
                    )}
                </div>
            ))}
        </div>
    </div>
);

export const ProcessSection = () => {
    const c = useContent();
    const p = c.process;
    if (!p) return null;
    const flows = p.flows || [];
    if (flows.length === 0) return null;

    return (
        <section className="bg-gray-50 border-y border-gray-200" data-testid="process-section">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
                    <div>
                        <div className="overline text-red-600 mb-4">{p.overline}</div>
                        <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight max-w-3xl">
                            {renderRich(p.heading)}
                        </h2>
                    </div>
                    <p className="text-gray-600 max-w-md">{p.paragraph}</p>
                </div>

                <div className="space-y-4 md:space-y-6">
                    {flows.map((flow, i) => (
                        <ProcessFlow key={i} flow={flow} idx={i} />
                    ))}
                </div>
            </div>
        </section>
    );
};
